import React, { useState } from 'react';
import clsx from 'clsx';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser, faUserGroup, faUserPlus, faUserTag } from '@fortawesome/pro-regular-svg-icons';
import { faCircleCheck } from '@fortawesome/pro-regular-svg-icons';
import Button from 'components/UI/V3/Button/Button';
import { useEmailMarketing } from '../../EmailMarketingProvider';
import { CampaignDraft, RecipientType } from '../../types';
import { AudienceData } from '../useAudienceData';
import { formatNumber } from '../../utils';
import { Panel } from '../../UI/Panel/Panel';
import { RadioCard } from '../../UI/RadioCard/RadioCard';
import TagsDrawer from './Audience/TagsDrawer';
import EditRecipientsDrawer from './Audience/EditRecipientsDrawer';
import StepHeading from './StepHeading';
import styles from './steps.module.scss';

interface AudienceStepProps {
    draft: CampaignDraft;
    audience: AudienceData;
    onChange: (patch: Partial<CampaignDraft>) => void;
}

// Same cards, order and copy as the marketplace Recipients step.
const RECIPIENT_OPTIONS: Array<{ id: RecipientType; title: string; description: string; icon: IconDefinition }> = [
    { id: 'all_profiles', title: 'All profiles', description: 'Send to every client and lead.', icon: faUserGroup },
    { id: 'leads', title: 'Leads', description: 'Send to every lead.', icon: faUserPlus },
    { id: 'all_clients', title: 'All clients', description: 'Send to every client.', icon: faUser },
    {
        id: 'client_groups',
        title: 'Client groups',
        description: 'Send to specific groups of clients by tag.',
        icon: faUserTag
    }
];

const ENTITY_LABELS: Record<RecipientType, { title: string; description: string; count: string }> = {
    all_profiles: {
        title: 'Total recipients',
        description: 'View and edit the recipients your email will be sent to.',
        count: 'recipients'
    },
    leads: {
        title: 'Total leads',
        description: 'View and edit the leads your email will be sent to.',
        count: 'leads'
    },
    all_clients: {
        title: 'Total clients',
        description: 'View and edit the clients your email will be sent to.',
        count: 'clients'
    },
    client_groups: {
        title: 'Total clients',
        description: 'View and edit the clients your email will be sent to.',
        count: 'clients'
    }
};

export const AudienceStep: React.FC<AudienceStepProps> = ({ draft, audience, onChange }) => {
    const { locations } = useEmailMarketing();
    const { estimate, cardCounts, tagOptions, poolSize, loading } = audience;
    const { known } = estimate;
    const [showBreakdown, setShowBreakdown] = useState(false);
    const [showTags, setShowTags] = useState(false);
    const [showEdit, setShowEdit] = useState(false);

    const selectedTagNames = tagOptions.filter(tag => draft.tags.includes(tag.id)).map(tag => tag.name);

    const needsTags = draft.recipientType === 'client_groups' && draft.tags.length === 0;
    const label = ENTITY_LABELS[draft.recipientType];

    // A new pool makes earlier picks meaningless, so any audience change resets them (same as the marketplace).
    const resetSelection = { selectedCustomerIds: 'all' as const, excludedCustomerIds: [] };

    return (
        <>
            <StepHeading
                title="Recipients"
                description="Choose who will receive your email. Each selected location sends it to its own customers."
            />

            <Panel title="Who receives it">
                <div className={styles.radioGrid}>
                    {RECIPIENT_OPTIONS.map(option => (
                        <RadioCard
                            key={option.id}
                            title={option.title}
                            description={option.description}
                            icon={option.icon}
                            checked={draft.recipientType === option.id}
                            meta={
                                option.id !== 'client_groups' &&
                                cardCounts?.[option.id] !== undefined &&
                                formatNumber(cardCounts[option.id] as number)
                            }
                            onSelect={() => onChange({ recipientType: option.id, tags: [], ...resetSelection })}
                        />
                    ))}
                </div>

                {draft.recipientType === 'client_groups' && (
                    <div className={styles.groupPicker}>
                        <span className={styles.groupTitle}>Select a group</span>
                        <button
                            type="button"
                            className={clsx(styles.groupOption, { [styles.groupOptionOn]: draft.tags.length > 0 })}
                            onClick={() => setShowTags(true)}
                        >
                            <span className={styles.groupRadio}>
                                {draft.tags.length > 0 && <span className={styles.groupRadioDot} />}
                            </span>
                            <span>
                                By client tag
                                {draft.tags.length > 0 && (
                                    <span className={styles.groupHint}> ({draft.tags.length} selected)</span>
                                )}
                            </span>
                        </button>
                        {selectedTagNames.length > 0 && (
                            <div className={styles.tagList}>
                                {selectedTagNames.map(name => (
                                    <span
                                        key={name}
                                        className={styles.tagChip}
                                    >
                                        {name}
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </Panel>

            {!known && (
                <div className={styles.total}>
                    <div className={styles.totalHeader}>
                        <div>
                            <h3>{label.title}</h3>
                            <p>
                                {loading
                                    ? `Counting ${label.count}…`
                                    : `Each location sends it to its own ${label.count}. The exact number is counted when the campaign is sent.`}
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {known && (
                <div className={styles.total}>
                    <div className={styles.totalHeader}>
                        <div>
                            <h3>{label.title}</h3>
                            <p>{label.description}</p>
                        </div>
                    </div>

                    <div className={styles.totalTop}>
                        <div className={styles.totalValue}>
                            {needsTags ? (
                                <strong>—</strong>
                            ) : (
                                <strong>
                                    {formatNumber(estimate.total)} <span>{label.count}</span>
                                </strong>
                            )}
                            {estimate.excluded > 0 && !needsTags && (
                                <small className={styles.excludedHint}>
                                    {formatNumber(estimate.excluded)} excluded
                                </small>
                            )}
                        </div>
                        <Button
                            variant="black"
                            label="Edit"
                            disabled={needsTags || poolSize === 0}
                            onClick={() => setShowEdit(true)}
                        />
                    </div>

                    {!needsTags && (
                        <ul className={styles.totalNotes}>
                            <li>
                                <FontAwesomeIcon icon={faCircleCheck} />
                                {formatNumber(estimate.duplicates)} shared customers get it once, from the location they
                                booked most recently
                            </li>
                            <li>
                                <FontAwesomeIcon icon={faCircleCheck} />
                                {formatNumber(estimate.unsubscribed)} customers unsubscribed from their location are
                                excluded
                            </li>
                        </ul>
                    )}

                    {!needsTags && estimate.byLocation.length > 0 && (
                        <button
                            type="button"
                            className={styles.breakdownToggle}
                            onClick={() => setShowBreakdown(prev => !prev)}
                        >
                            {showBreakdown ? 'Hide breakdown' : 'View by location'}
                        </button>
                    )}

                    {showBreakdown && !needsTags && (
                        <ul className={styles.breakdown}>
                            {estimate.byLocation.map(({ location, count }) => (
                                <li key={location.id}>
                                    <span>
                                        {location.name}
                                        <small>{location.city}</small>
                                    </span>
                                    <strong>{formatNumber(count)}</strong>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            )}

            {showTags && (
                <TagsDrawer
                    tags={tagOptions}
                    selectedIds={draft.tags}
                    onSave={tags => onChange({ tags, ...resetSelection })}
                    onClose={() => setShowTags(false)}
                />
            )}

            {showEdit && (
                <EditRecipientsDrawer
                    selection={audience.selection}
                    poolSize={poolSize}
                    locations={locations}
                    locationIds={draft.locationIds}
                    selectedIds={draft.selectedCustomerIds}
                    excludedIds={draft.excludedCustomerIds}
                    onSave={(selectedCustomerIds, excludedCustomerIds) =>
                        onChange({ selectedCustomerIds, excludedCustomerIds })
                    }
                    onClose={() => setShowEdit(false)}
                />
            )}
        </>
    );
};

export default AudienceStep;
